// src/dataTypes/productManagment/types.ts

export interface ProductMedia {
  id              : number | string;
  name            : string | null;
  rank            : number;
  publicFileName  : string ,
  privateFileName : string , 
  asThumbnail     : boolean ,
  mediaType       : string ,
  fileContent     : File | null;
  tag             : string ,
}

export interface ProductCharacteristic {
  id          : number | string;
  name        : string | null;
  kind        : string | null;
  unit        : string | null;
  value       : string | null;
  rank            : number;  
  tag             : string ,
}

export interface ProductDiscount {
  id             : number;
  type           : string;
  condition      : string;   //  si type === statut, alors nom du statut minimale, si type === evenement, alors nom de l'événement
  value          : string;   //  "x en valeur normale ou x %"
  active         : boolean;  //  s'il faut tenir compte de
  priceToApplyOn : string;   // est-ce que c'est sur le prix HT ou prix TTC
  priceToPay     : number;
}

export interface ProductPrice {
  id             : number;
  name           : string | null;
  requiredStatus : string | null;
  comment        : string | null;
  code           : string | null;
  active         : boolean;
  creationDate   : Date;
  basePrice      : number;
  taxRate        : number;
  priceWithTax   : number;
  discounts      : ProductDiscount [];
}

export interface ProductVariant {
  id:              number;
  name:            string | null;
  complement:      string | null;
  description:     string | null;
  code:            string | null;
  barcode:         string | null;
  active:          boolean;
  stockAvailable:  number;
  stockBooked:     number;
  stockAssigned:   number;
  stockThreshold:  number;
  creationDate:    Date;
  characteristics: ProductCharacteristic[];
  medias:          ProductMedia[];
  prices:          ProductPrice[];
  // Présence = ce palier est proposé en abonnement récurrent (SubscriptionCreation),
  // absence (null/undefined) = achat ponctuel uniquement — cf.
  // ProductVariant::recurringIntervalUnit côté API. 'month'|'year'|... pour l'unité, cf.
  // BankProcessor_Stripe/Paypal. Optionnels : de nombreux appelants construisent un
  // ProductVariant à la main (formulaires d'édition du catalogue) sans jamais toucher à
  // la récurrence — seul mapVariants() (api_product.ts) les renseigne vraiment.
  recurringIntervalUnit?:  string | null;
  recurringIntervalCount?: number | null;
  // Id du Billing Plan Paypal ("P-...") de ce palier, si récurrent — cf.
  // ProductVariantCreatePaypalPlan.php. null tant qu'il n'a pas été créé (bouton "Créer
  // le plan Paypal", ProductSheet_ProductVariant).
  externalPaypalPlanId?: string | null;
}

export interface ProductCategory {
  id:              number
  name:            string
  rank:            number
  creationDate:    Date;
  visible:         boolean;
}

export interface Product {
  id                              : number;
  uuid                            : string;
  publicRef                       : string;
  name                            : string;
  catchword                       : string | null;
  code                            : string | null;
  publicPortal                    : boolean     ,    
  active                          : boolean;
  activeScheduled                 : boolean;
  activeStartDate                 : Date | null;
  activeEndDate                   : Date | null;
  creationDate                    : Date;
  creationIpAddress?              : string;
  lastUpdateDate                  : Date | null;
  requiredStatus                  : string | null;
  requiredUserStatusId            : number      ,     
  paymentRequired                 : boolean;
  kind                            : string | null;
  description                     : string | null;
  variants                        : ProductVariant[];
  characteristics                 : ProductCharacteristic[];
  medias                          : ProductMedia[];
  categoriesIds                   : number [];
  associatedMediaId               : number;
  associatedBroadcastingChannelId : number;
  associatedUserStatusId          : number;
  associatedAdminResource         : string | null;   // kind === 'CreatorCapacity' : 'disk'|'devices'|'channels'|'broadcasting'|'recording'
}

export const CharacteristicTypeOptions = ['Dimension', 'Poids', 'Durée', 'Couleur', 'URL', 'Quantité'] as const;

export const CharacteristicUnitOptions: Record<(typeof CharacteristicTypeOptions)[number], string[]> = {
    Dimension : ['m', 'cm', 'mm'],
    Poids     : ['kg', 'g'],
    Durée     : ['h', 'm', 's'],
    Couleur   : [''], // Empty for color
    URL       : [''],
    Quantité  : ['Go', 'unité', 'heure'],
  };


export const initializedCharacteristic = (): ProductCharacteristic => ({
  id          : 0,
  name        : '',
  kind        : 'Dimension',
  unit        : 'm',
  value       : '',
  rank        : 1,  
  tag         : '' ,
});

export const initializedMedia = (): ProductMedia => ({
  id              : 0,
  rank            : 0,
  name            : '',
  publicFileName  : '' ,
  privateFileName : '' , 
  asThumbnail     : false ,
  mediaType       : '' ,
  fileContent     : null ,
  tag             : ''
});

export const initializedDiscount = (): ProductDiscount => ({
  id             : 0,
  type           : '',
  condition      : '',
  value          : '',
  active         : true,
  priceToApplyOn : '',
  priceToPay     : 0,
});

export const initializedPrice = (): ProductPrice => ({
  id:                            0,
  name:                          'Premier prix',
  requiredStatus:                null,
  comment:                       null,
  code:                          'p1',
  active:                        true,
  creationDate:                  new Date(),
  basePrice:                     0,
  taxRate:                       20,
  priceWithTax:                  0,
  discounts:                     [],
});

export const initializedProductVariant = (providedCode?: string): ProductVariant => ({
  id:              0,
  name:            providedCode ? `Variant ${providedCode}` : 'Produit initial',
  complement:      '',
  description:     '',
  code:            providedCode ? `v${providedCode}` : 'v0',
  barcode:         '',
  active:          true,
  creationDate:    new Date(),
  stockAvailable:  0,
  stockBooked:     0,
  stockAssigned:   0,
  stockThreshold:  0,
  characteristics: [],
  medias:          [],
  prices:          [initializedPrice()],
  recurringIntervalUnit:  null,
  recurringIntervalCount: null,
  externalPaypalPlanId:   null,
});
export interface ProductCategory {
  id:              number
  name:            string
  rank:            number
  creationDate:    Date;
  visible:         boolean;
}

export const initializedProductCategory = (): ProductCategory => ({
  id:                0,
  name:              '',
  rank:              0,
  creationDate:      new Date(),
  visible:           false
});

export const initializedProduct = (): Product => ({
  id:                   0 ,
  uuid:                 '',
  publicRef:                 '',
  name:                 '',
  catchword:            '',
  code:                 '',
  publicPortal:         false ,     // Présent sur le portail public
  active:               false,
  activeScheduled:      false,
  activeStartDate:      null,
  activeEndDate:        null,
  creationDate:         new Date(),
  creationIpAddress:    '',
  lastUpdateDate:       null,
  requiredStatus:       'visitor',
  requiredUserStatusId: 1      ,     // Id du statut que doit avoir le user 
  paymentRequired:      false,
  kind:                 'Physical Item',
  description:          '',
  variants:             [initializedProductVariant()],
  characteristics:      [],
  medias:               [],
  categoriesIds:        [],
  associatedMediaId               : 0,
  associatedBroadcastingChannelId : 0,
  associatedUserStatusId          : 0,
  associatedAdminResource         : null,
});
